import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormFeedback,
} from 'reactstrap';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';
import {
  disableOwnTotp,
  fetchViewerMfaStatus,
  setOwnEmailOtpPreference,
} from '../../helpers/wordpress_totp';
import { getAuthUser } from '../../helpers/auth_storage';

const SecuritySettings = () => {
  document.title = 'Security Settings | NGK Tours';
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState<string | null>(null);
  const justEnabled = searchParams.get('totp') === '1';

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await fetchViewerMfaStatus();
      setTotpEnabled(status.totpEnabled);
      setEmailOtpEnabled(status.emailOtpEnabled);
    } catch (err: any) {
      setError(err?.message || 'Unable to load security status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const authUser = getAuthUser();

  const displayName = user?.name || authUser?.user?.name || 'Staff user';
  const displayEmail = user?.email || authUser?.user?.email || '';

  const onToggleEmailOtp = async (nextEnabled: boolean) => {
    setSuccess(null);
    setError(null);
    if (!nextEnabled && !totpEnabled) {
      setError(
        'You must keep Email OTP or Authenticator 2FA enabled. Set up authenticator 2FA first.'
      );
      return;
    }
    setSaving(true);
    try {
      const status = await setOwnEmailOtpPreference(nextEnabled);
      setEmailOtpEnabled(status.emailOtpEnabled);
      setTotpEnabled(status.totpEnabled);
      setSuccess(
        nextEnabled
          ? 'Email OTP is now enabled for your account.'
          : 'Email OTP is now off. Authenticator 2FA is required at login.'
      );
    } catch (err: any) {
      setError(err?.message || 'Unable to update Email OTP.');
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDisableTotp = async () => {
    setDisableError(null);
    if (!disableCode.trim()) {
      setDisableError('Enter your authenticator or recovery code.');
      return;
    }
    if (!emailOtpEnabled) {
      setDisableError(
        'Turn on Email OTP before disabling authenticator 2FA.'
      );
      return;
    }
    setSaving(true);
    try {
      const status = await disableOwnTotp(disableCode.trim());
      setTotpEnabled(status.totpEnabled);
      setEmailOtpEnabled(status.emailOtpEnabled);
      setDisableOpen(false);
      setDisableCode('');
      setSuccess('Authenticator 2FA has been turned off. Email OTP remains on.');
    } catch (err: any) {
      setDisableError(err?.message || 'Unable to turn off authenticator 2FA.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="NGK Tours" breadcrumbItem="Security Settings" />

        {justEnabled && (
          <Alert color="success">
            Authenticator 2FA is now enabled on your account.
          </Alert>
        )}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        <Alert color="info">
          At least one of <strong>Email OTP</strong> or{' '}
          <strong>Authenticator 2FA</strong> must stay enabled.
        </Alert>

        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-1">Account</h4>
                <p className="text-muted mb-0">{displayName}</p>
                {displayEmail && (
                  <p className="text-muted mb-0 small">{displayEmail}</p>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h4 className="card-title mb-1">Email OTP</h4>
                    <p className="text-muted mb-0">
                      A 6-digit code is emailed after password login.
                    </p>
                  </div>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <Badge color={emailOtpEnabled ? 'success' : 'secondary'} pill>
                      {emailOtpEnabled ? 'Enabled' : 'Off'}
                    </Badge>
                  )}
                </div>

                {!loading && !emailOtpEnabled && (
                  <Alert color="warning" className="mt-3 mb-3">
                    Email OTP is off. Authenticator 2FA is required when you sign
                    in.
                  </Alert>
                )}

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {emailOtpEnabled ? (
                    <Button
                      color="danger"
                      outline
                      disabled={loading || saving || !totpEnabled}
                      onClick={() => onToggleEmailOtp(false)}
                      title={
                        !totpEnabled
                          ? 'Set up authenticator 2FA before turning off Email OTP'
                          : undefined
                      }
                    >
                      Turn off Email OTP
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      disabled={loading || saving}
                      onClick={() => onToggleEmailOtp(true)}
                    >
                      Turn on Email OTP
                    </Button>
                  )}
                  <Button color="light" onClick={loadStatus} disabled={loading || saving}>
                    Refresh status
                  </Button>
                </div>

                <p className="small text-muted mb-0 mt-3">
                  You can turn Email OTP off only if Authenticator 2FA is already
                  enabled.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h4 className="card-title mb-1">Authenticator app (2FA)</h4>
                    <p className="text-muted mb-0">
                      Google Authenticator, Authy, or similar.
                    </p>
                  </div>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <Badge color={totpEnabled ? 'success' : 'warning'} pill>
                      {totpEnabled ? 'Enabled' : 'Not set up'}
                    </Badge>
                  )}
                </div>

                {!loading && totpEnabled && (
                  <Alert color="success" className="mt-3 mb-3">
                    Your authenticator app is linked. You will be asked for a
                    6-digit code (or recovery code) at login.
                  </Alert>
                )}

                {!loading && !totpEnabled && (
                  <Alert color="warning" className="mt-3 mb-3">
                    Authenticator 2FA is optional while Email OTP is on. You can
                    set it up for stronger protection.
                  </Alert>
                )}

                <div className="d-flex flex-wrap gap-2">
                  {!totpEnabled && (
                    <Button color="primary" tag={Link} to="/totp-setup">
                      Set up authenticator
                    </Button>
                  )}
                  {totpEnabled && (
                    <Button
                      color="danger"
                      outline
                      disabled={loading || saving || !emailOtpEnabled}
                      onClick={() => {
                        setDisableError(null);
                        setDisableCode('');
                        setDisableOpen(true);
                      }}
                      title={
                        !emailOtpEnabled
                          ? 'Turn on Email OTP before disabling authenticator 2FA'
                          : undefined
                      }
                    >
                      Turn off 2FA
                    </Button>
                  )}
                  <Button color="light" onClick={loadStatus} disabled={loading || saving}>
                    Refresh status
                  </Button>
                </div>

                <p className="small text-muted mb-0 mt-3">
                  Lost your phone? Ask a Super Admin to reset your 2FA from User
                  Management → Edit User. Then set it up again here.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h4 className="card-title mb-1">Password</h4>
                <p className="text-muted">
                  Use forgot password on the login screen if you need to reset
                  your password via email.
                </p>
                <Button color="light" tag={Link} to="/recoverpw">
                  Request password reset email
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={disableOpen} toggle={() => !saving && setDisableOpen(false)}>
        <ModalHeader toggle={() => !saving && setDisableOpen(false)}>
          Turn off authenticator 2FA
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Enter a current authenticator code (or a recovery code) to confirm.
            Email OTP will stay enabled.
          </p>
          <Label for="disable-totp-code">Verification code</Label>
          <Input
            id="disable-totp-code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="6-digit code or recovery code"
            invalid={!!disableError}
            disabled={saving}
            autoComplete="one-time-code"
          />
          {disableError && <FormFeedback className="d-block">{disableError}</FormFeedback>}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDisableOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="danger" onClick={onConfirmDisableTotp} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Turn off 2FA'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SecuritySettings;
